import React, { useEffect, useRef, useState } from "react";
import { useMutation } from "react-query";
import { useAppContext } from "../context/appContext";
import idApi from "../service/IdApi";
import { throttle } from "lodash";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Mixpanel } from "../utils/Mixpanel";
import { InputText } from "primereact/inputtext";
import DataUtils from "../utils/DataUtils";

const AddEmailFriend = ({ showAddEmailFriend, hideAddEmailFriend, friendAdded }) => {
    const { displayAlert } = useAppContext();
    const [isVisible, setIsVisible] = useState(false);
    const [isAddEmailFriendProcessing, setIsAddEmailFriendProcessing] = useState(false);
    const [email, setEmail] = useState("");
    const [step, setStep] = useState(1);
    const [validEmail, setValidEmail] = useState(false);
    const emailInputRef = useRef(null);

    const addEmailFriendMutation = useMutation(() => {
        try {
            var body = {};
            body.email = email.trim();
            body.inviteMethod = "e";
            return idApi.post("auth/user/add-friend", body);
        } catch (error) {
            throw error;
        }
    });

    useEffect(() => {
        if (showAddEmailFriend) {
            setStep(1);
            setEmail("");
            setIsVisible(true);
            setValidEmail(false);
        }
        else {
            setIsVisible(false);
        }
    }, [showAddEmailFriend]);

    const addEmailFriend = throttle(() => {
        setIsAddEmailFriendProcessing(true);
        addEmailFriendMutation.mutate({}, {
            onError: (error) => {
                console.error('Failed to add email friend:', error);
                var mixpanelMessage = "Error adding email friend: " + error;
                Mixpanel.track("UI ID - Javascript error", {
                    'Javascript Error': mixpanelMessage
                });
                displayAlert("error", "Unexpected Error", "Unexpected error. Please try again later");
                setIsAddEmailFriendProcessing(false);
                hideAddEmailFriend();
            },
            onSuccess: (data) => {
                if (data.data.isSelf) {
                    displayAlert("error", "Error", "You cannot add yourself as a friend");
                    setIsAddEmailFriendProcessing(false);
                    return;
                }
                if (data.data.alreadyFriends) {
                    displayAlert("error", "Error", "User is already a friend");
                    setIsAddEmailFriendProcessing(false);
                    return;
                }
                if (data.data.alreadyInvited) {
                    displayAlert("error", "Error", "User already invited");
                    setIsAddEmailFriendProcessing(false);
                    return;
                }
                friendAdded();
                displayAlert("success", "Success", "Friend request sent");
                setStep(2);
                setIsAddEmailFriendProcessing(false);
            },
        });
    }, 500, { trailing: false });

    const handleEmailChange = (e) => {
        setEmail(e.target.value);
        if (DataUtils.isEmailValid(e.target.value, true)) {
            setValidEmail(true);
        }
        else {
            setValidEmail(false);
        }
    };

    return (
        <div>
            <Dialog
                visible={isVisible}
                style={{ width: '425px' }}
                breakpoints={{ '641px': '95vw' }}
                header="Add Friend by Email"
                modal
                onHide={hideAddEmailFriend}
                className="p-fluid"
                onShow={() => {
                    if (emailInputRef.current) {
                        try {
                            emailInputRef.current.focus();
                        }
                        catch (e) {
                        }
                    }
                }}
            >
                {step === 1 && (
                    <div>
                        <div className="flex flex-column">
                            <div>
                                Enter the email address of the friend you want to add. Once they accept the invitation, you will be able to see each other's recommendations!
                            </div>
                            <div className="mt-3">
                                <InputText
                                    value={email}
                                    onChange={handleEmailChange}
                                    autoFocus
                                    placeholder="Email Address"
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && validEmail) {
                                            addEmailFriend();
                                        }
                                    }}
                                    ref={emailInputRef}
                                />
                            </div>
                            {/* <div className={classNames("mt-3", { "text-500": !validEmail })}>Click the Invite Friend button to continue</div> */}
                        </div>
                        <div className="flex mt-3 w-full justify-content-end">
                            <div className="mr-2">
                                <Button label="Cancel" icon="pi pi-times" className="p-button-text p-button-danger" onClick={hideAddEmailFriend} />
                            </div>
                            <div>
                                <Button label="Add Friend" icon="pi pi-check" onClick={addEmailFriend} loading={isAddEmailFriendProcessing} disabled={!validEmail} />
                            </div>
                        </div>
                    </div>
                )}
                {step === 2 && (
                    <div>
                        <div className="flex flex-column">
                            <div className="flex flex-row text-green-400 align-items-center text-lg">
                                <div className="mr-2">
                                    <i className="pi pi-check-circle" style={{ fontSize: '1.2rem' }}></i>
                                </div>
                                <div className="text-green-400">
                                    An invitation has been sent to the user.
                                </div>
                            </div>
                            <div className="mt-2">
                                We will notify you when they accept the invitation, allowing you to see each other's recommendations!
                            </div>
                        </div>
                        <div className="flex mt-3 w-full justify-content-end">
                            <div>
                                <Button label="Close" icon="pi pi-times" onClick={hideAddEmailFriend} />
                            </div>
                        </div>
                    </div>
                )}
            </Dialog>
        </div>
    );
};

export default AddEmailFriend;
